import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Icon, type IconName } from '../components/icons';
import { Input } from '../components/ui';
import { Application } from '../help/topics/Application';
import { Setup } from '../help/topics/Setup';
import { Developer } from '../help/topics/Developer';
import { Faq } from '../help/topics/Faq';
import { Changelog } from '../help/topics/Changelog';
import { Licensing } from '../help/topics/Licensing';

interface Topic {
  slug: string;
  title: string;
  blurb: string;
  icon: IconName;
  render: () => ReactNode;
}

export const HELP_TOPICS: [Topic, ...Topic[]] = [
  {
    slug: 'application',
    title: 'Application documentation',
    blurb: 'Using the studio, module by module: builder, logic, scoring, sharing, responses.',
    icon: 'book',
    render: () => <Application />,
  },
  {
    slug: 'setup',
    title: 'Setup guide',
    blurb: 'Putting a FormForge form into your own application, and everything the embed can do.',
    icon: 'grid',
    render: () => <Setup />,
  },
  {
    slug: 'developer',
    title: 'Developer documentation',
    blurb: 'Integrating with your systems: API keys, the REST API, webhooks, the runtime protocol.',
    icon: 'function',
    render: () => <Developer />,
  },
  {
    slug: 'faq',
    title: 'FAQ',
    blurb: 'Questions that come up repeatedly, and the honest answers.',
    icon: 'help',
    render: () => <Faq />,
  },
  {
    slug: 'changelog',
    title: 'Change log',
    blurb: 'Notable changes, written by hand as work lands.',
    icon: 'clock',
    render: () => <Changelog />,
  },
  {
    slug: 'licensing',
    title: 'Licensing',
    blurb: 'The licence for FormForge, and attributions for everything it ships.',
    icon: 'shield',
    render: () => <Licensing />,
  },
];

/**
 * The help section.
 *
 * Its own contents rail rather than more entries in the application rail: these are six
 * long documents that are read one after another, and mixing them into navigation people
 * use twenty times a day would cost more than it gives. The topic is a route parameter, so
 * a page of the documentation can be linked to directly.
 */
export function Help() {
  const { topic } = useParams();
  const [filter, setFilter] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const active = HELP_TOPICS.find((t) => t.slug === topic) ?? HELP_TOPICS[0];

  const matches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return HELP_TOPICS;
    return HELP_TOPICS.filter(
      (t) => t.title.toLowerCase().includes(q) || t.blurb.toLowerCase().includes(q),
    );
  }, [filter]);

  // A new document starts at its beginning, not wherever the last one was scrolled to.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [active.slug]);

  return (
    <div className="flex min-h-full">
      <nav
        aria-label="Documentation"
        className="hidden w-[248px] shrink-0 border-r border-[var(--ff-border)]
                   bg-[var(--ff-surface)] p-3 md:block"
      >
        <p className="ff-eyebrow mb-2 ml-1.5">Documentation</p>

        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter topics"
          aria-label="Filter documentation topics"
          className="mb-2 h-8 text-[13px]"
        />

        <ul className="space-y-0.5">
          {matches.map((t) => (
            <li key={t.slug}>
              <NavLink
                to={`/help/${t.slug}`}
                className={({ isActive }) => clsx(
                  'flex items-start gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                  isActive || t.slug === active.slug
                    ? 'bg-[var(--ff-surface-raised)] text-[var(--ff-text)] shadow-[inset_2px_0_0_var(--ff-accent)]'
                    : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]',
                )}
              >
                <Icon name={t.icon} size={15} className="mt-0.5 shrink-0" />
                <span className="min-w-0">{t.title}</span>
              </NavLink>
            </li>
          ))}

          {matches.length === 0 && (
            <li className="px-2.5 py-3 text-[13px] text-[var(--ff-text-muted)]">
              Nothing matches “{filter}”.
            </li>
          )}
        </ul>

        <p className="mt-4 border-t border-[var(--ff-border)] px-1.5 pt-3 text-[12px]
                      leading-relaxed text-[var(--ff-text-faint)]">
          The design documents behind these pages live in <span className="ff-mono">docs/</span>{' '}
          in the repository.
        </p>
      </nav>

      <div ref={contentRef} className="min-w-0 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-3xl px-6 py-7">
          <header className="mb-6">
            <p className="ff-eyebrow">Help</p>
            <h1 className="mt-1 flex items-center gap-2.5 text-[22px] font-semibold
                           leading-tight tracking-[-0.01em]">
              <Icon name={active.icon} size={19} className="text-[var(--ff-text-muted)]" />
              {active.title}
            </h1>
          </header>

          {/* Small screens lose the rail, so the topics have to be reachable some other way. */}
          <div className="mb-6 flex flex-wrap gap-1.5 md:hidden">
            {HELP_TOPICS.map((t) => (
              <NavLink
                key={t.slug}
                to={`/help/${t.slug}`}
                className={clsx(
                  'rounded-full border px-2.5 py-1 text-[12.5px]',
                  t.slug === active.slug
                    ? 'border-[var(--ff-primary)] text-[var(--ff-primary)]'
                    : 'border-[var(--ff-border)] text-[var(--ff-text-muted)]',
                )}
              >
                {t.title}
              </NavLink>
            ))}
          </div>

          <article className="text-[14px] leading-relaxed">{active.render()}</article>
        </div>
      </div>
    </div>
  );
}
